import React, { useState, useEffect } from "react";
import {
  Title,
  Table,
  Button,
  Badge,
  Group,
  Text,
  Loader,
  Center,
  Paper,
  ScrollArea,
  ThemeIcon,
  Tooltip,
  ActionIcon
} from "@mantine/core";
import { 
  IconRefresh, 
  IconBuilding, 
  IconChefHat, 
  IconStethoscope,
  IconClipboardCheck,
  IconEye
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

// Helper Warna Status
const statusColors = {
  diterima: 'green',
  dikirim: 'blue',
  disiapkan: 'yellow',
  dibatalkan: 'red'
};

export default function DistribusiSupervisorPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // --- 1. FETCH DATA ---
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/supervisor/distribusi");
      const hasil = response.data.data || []; 
      setData(hasil);
    } catch (error) {
      console.error("Gagal mengambil data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- 2. HANDLERS ---
  const goToNutrisi = () => {
    navigate("/supervisor/laporan-nutrisi");
  };

  const goToMonitoring = () => {
    navigate("/supervisor/laporan-monitoring");
  };

  // --- 3. RENDER ROWS ---
  const rows = data.map((item) => (
    <Table.Tr key={item.id}>
      {/* Tanggal */}
      <Table.Td>
        <Text fz="sm" fw={500}>
            {new Date(item.tanggal_distribusi).toLocaleDateString('id-ID', {
                weekday: 'short', day: 'numeric', month: 'short'
            })}
        </Text>
        <Text fz="xs" c="dimmed">
            {new Date(item.tanggal_distribusi).getFullYear()}
        </Text>
      </Table.Td>

      {/* Sekolah Tujuan */}
      <Table.Td>
        <Group gap="sm">
            <ThemeIcon color="blue" variant="light" radius="xl" size="lg">
                <IconBuilding size={18}/>
            </ThemeIcon>
            <div>
                <Text fz="sm" fw={500}>{item.sekolah?.nama_sekolah || "Sekolah Dihapus"}</Text>
                <Text fz="xs" c="dimmed">NPSN: {item.sekolah?.npsn || "-"}</Text>
            </div>
        </Group>
      </Table.Td>

      {/* Penyedia & Menu */}
      <Table.Td>
        <Group gap="sm">
            <ThemeIcon color="orange" variant="light" radius="xl" size="lg">
                <IconChefHat size={18}/>
            </ThemeIcon>
            <div>
                <Text fz="sm" fw={500}>{item.menu?.nama_menu || "Menu Dihapus"}</Text>
                <Text fz="xs" c="dimmed">{item.penyedia?.nama_perusahaan || "Penyedia Dihapus"}</Text>
            </div>
        </Group>
      </Table.Td>

      {/* Status Pengiriman */}
      <Table.Td>
        <Badge 
            color={statusColors[item.status] || 'gray'} 
            variant="light"
        >
            {item.status.toUpperCase()}
        </Badge>
      </Table.Td>

      {/* Status Konfirmasi Sekolah */}
      <Table.Td>
         {item.konfirmasi_penerimaan ? (
            <Badge color="green" variant="dot">Sudah Diterima</Badge>
         ) : (
            <Badge color="gray" variant="outline">Belum Sampai</Badge>
         )}
      </Table.Td>

      {/* Aksi */}
      <Table.Td>
        <Group gap={8} justify="flex-end">
            <Tooltip label="Input Laporan Nutrisi">
                <ActionIcon 
                    variant="light" 
                    color="teal" 
                    onClick={goToNutrisi}
                    size="md"
                >
                    <IconStethoscope size={18}/>
                </ActionIcon>
            </Tooltip>

            <Tooltip label="Input Laporan Monitoring">
                <ActionIcon 
                    variant="light" 
                    color="blue" 
                    onClick={goToMonitoring}
                    size="md"
                >
                    <IconClipboardCheck size={18}/>
                </ActionIcon>
            </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  // --- RENDER UTAMA ---
  if (loading) return <Center h="80vh"><Loader size="lg"/></Center>;

  return (
    // Menggunakan div padding 20 agar konsisten dengan layout Admin lainnya
    <div style={{ padding: 20 }}>
      
      <Group justify="space-between" mb="lg">
        <div>
           <Title order={2}>Monitoring Distribusi</Title>
           <Text c="dimmed" size="sm">Pantau jadwal pengiriman dan input laporan lapangan</Text>
        </div>
        <Button variant="light" onClick={fetchData} leftSection={<IconRefresh size={16}/>}>
            Refresh Data
        </Button>
      </Group>

      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        <ScrollArea>
            <Table highlightOnHover verticalSpacing="sm">
                <Table.Thead bg="gray.0">
                    <Table.Tr>
                        <Table.Th>Tanggal</Table.Th>
                        <Table.Th>Sekolah Tujuan</Table.Th>
                        <Table.Th>Menu & Penyedia</Table.Th>
                        <Table.Th>Status Kirim</Table.Th>
                        <Table.Th>Konfirmasi Sekolah</Table.Th>
                        <Table.Th ta="right">Aksi</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.length > 0 ? rows : (
                        <Table.Tr>
                            <Table.Td colSpan={6} align="center" style={{padding: 30}}>
                                <Text c="dimmed">Belum ada data distribusi.</Text>
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </ScrollArea>
      </Paper>
    </div>
  );
}